import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Checkbox,
  FormControlLabel,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";
import styles from "@/components/document/createDocumentModal.module.css";
import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useForm, Controller } from "react-hook-form";
import { SubjectSearchSelect } from "@/components/subjectSearch/subjectSearchSelect";
import { useSubjectSearch } from "@/helper/customHooks/useSubjectSearch";
import { InputWithRef } from "@/components/forms/inputsWithRef/inputWithRef";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DropDown from "@/components/forms/inputs/dropDown";
import { api } from "@/helper/api";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";

import { useTemplateParameters } from "@/components/documentTemplate/useTemplateParameters";
import TemplateParameterFields from "@/components/documentTemplate/TemplateParameterFields";
import type { IDropDownOption } from "@/types/DropDown";

interface CreateDocumentModalProps {
  modalOpen: boolean;
  onClose: () => void;
  selectedTableRows: any[];
  handleSubmitFormData: (data: any) => void;
}

const CreateDocumentModal: FC<CreateDocumentModalProps> = ({
  modalOpen,
  onClose,
  selectedTableRows,
  handleSubmitFormData,
}) => {
  const { t } = useTranslation();
  const [folderOptions, setFolderOptions] = useState<any[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    shouldFocusError: false,
    defaultValues: {
      template: null as IDropDownOption | null,
      filename_template: "",
      is_exclusion_list: false,
      include_children: false,
      parents_only: false,
      save_in_subject_documents: false,
      target_folder: "",
      overwrite_existing_file: false,
    },
  });

  const selectedTemplate = watch("template");
  const templateId = selectedTemplate?.value
    ? String(selectedTemplate.value)
    : undefined;
  const parameters = useTemplateParameters(templateId, modalOpen);
  const shouldSaveinSubjectDocuments = watch("save_in_subject_documents");
  const parentsOnlySelected = watch("parents_only");
  const includeChildrenSelected = watch("include_children");
  const isExclusionList = watch("is_exclusion_list");
  const treeNameForGetRequest = "default_manage";
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField({
      fieldOrder: ["template", "filename_template", "target_folder"],
    });

  useEffect(() => {
    if (modalOpen) {
      reset();
    }
  }, [modalOpen, reset]);

  useEffect(() => {
    if (!shouldSaveinSubjectDocuments) return;

    getFolders();
  }, [shouldSaveinSubjectDocuments]);

  const getFolders = async () => {
    try {
      const response: any = await new Promise<void>((resolve, reject) => {
        api.genericApiRequest({
          entity: "folder",
          method: "get",
          successHandler: (response: any) => resolve(response),
          parametersToRender: {
            additionalUrlParameters: { tree__name: treeNameForGetRequest },
          },
          failHandler: (error: any) => reject(error),
        });
      });
      const data = response.data.results;
      const tempFolderData = data.map((folder: any) => ({
        value: folder.id,
        label: folder.name,
      }));
      setFolderOptions(tempFolderData);
    } catch (error) {
      console.log(error);
    }
  };

  const onSubmit = (data: any) => {
    const templateParameters = parameters.validate();
    if (templateParameters === null) return;
    const tempCompanySubjectSet =
      selectedTableRows.length > 0
        ? selectedTableRows
            .map((row: any) => row.subject?.id || row.subject_id)
            .filter(Boolean)
        : [];

    const submitData: any = {
      template: data.template?.value,
      template_parameters: templateParameters,
      company_subject_set: tempCompanySubjectSet,
      filename_template: `${data.filename_template}_{{entity.name|safe}}`,
      is_exclusion_list: data.is_exclusion_list,
      include_children: data.include_children,
      parents_only: data.parents_only,
    };

    if (data.save_in_subject_documents) {
      submitData.save_in_subject_documents = data.save_in_subject_documents;
      submitData.target_folder = data.target_folder;
    }

    if (data.overwrite_existing_file) {
      submitData.overwrite_existing_file = data.overwrite_existing_file;
    }

    handleSubmitFormData(submitData);
  };

  const { fetchSubjects } = useSubjectSearch({
    subdomain: "admin",
    entity: "documentTemplate",
  });

  const getTranslationKey = () => {
    const count = selectedTableRows.length;

    if (count === 0) {
      if (parentsOnlySelected)
        return "no_companies_selected_create_for_all_parents_only";
      return "no_companies_selected_create_for_all";
    }

    if (isExclusionList) {
      if (parentsOnlySelected) {
        return "create_document_for_all_companies_except_selected_parents_only";
      }
      if (includeChildrenSelected) {
        return "create_document_for_all_companies_except_selected_include_children";
      }
      return "create_document_for_all_companies_except_selected_companies";
    }

    if (parentsOnlySelected && !isExclusionList) {
      return "create_document_for_selected_companies_parents_only";
    }

    if (includeChildrenSelected) {
      return "create_document_for_all_selected_companies_include_children";
    }

    return "create_document_for_selected_companies";
  };

  useModalDismiss(modalOpen, onClose);

  return (
    <Modal open={modalOpen} onClose={onClose} disableAutoFocus>
      <Box className={styles.createDocumentModal}>
        <Typography variant="h6" component="h2" gutterBottom align="center">
          {t("create_document")}
        </Typography>
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit, (errors) => {
            parameters.validate();
            focusFirstInvalidField(errors);
          })}
        >
          <Box className={styles.formContent}>
            <Controller
              name="template"
              control={control}
              rules={{ required: t("field_required") as string }}
              render={({ field: { onChange, value, name } }) => (
                <div ref={registerValidationTarget("template")}>
                  <FormFieldLabel
                    label={t("document_template")}
                    htmlFor={name}
                    mandatory
                  />
                  <SubjectSearchSelect
                    value={value}
                    name={name}
                    loadOptions={(input: any) =>
                      fetchSubjects(input, {
                        filterParameter: {
                          type: "template",
                          exclude_measurement_dependent: true,
                          name__icontains: input,
                        },
                        mapResponseToOptions: (data) =>
                          data.results.map((item: any) => ({
                            value: item.id,
                            label: `${item.name}`,
                          })),
                      })
                    }
                    cacheOptions={false}
                    onChange={onChange}
                    isClearable={true}
                    placeholder={t("choose_template")}
                  />
                </div>
              )}
            />
            {errors.template && (
              <Typography color="error" variant="caption">
                {errors.template.message}
              </Typography>
            )}

            {parameters.loading && (
              <CircularProgress
                size={20}
                aria-label={t("document_parameters.loading")}
              />
            )}
            {parameters.failed && (
              <Alert
                severity="error"
                action={
                  <Button onClick={parameters.retry}>
                    {t("document_parameters.retry")}
                  </Button>
                }
              >
                {t("document_parameters.load_error")}
              </Alert>
            )}
            {parameters.definitions.length > 0 && (
              <Box sx={{ my: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  {t("document_parameters.title")}
                </Typography>
                <TemplateParameterFields
                  definitions={parameters.definitions}
                  values={parameters.values}
                  errors={parameters.errors}
                  onChange={parameters.onChange}
                />
              </Box>
            )}

            <Controller
              name="filename_template"
              control={control}
              rules={{ required: t("field_required") as string }}
              render={({ field }) => (
                <div ref={registerValidationTarget("filename_template")}>
                  <InputWithRef
                    {...field}
                    name={field.name}
                    onChange={field.onChange}
                    label={t("file_name")}
                    ref={field.ref}
                    value={field.value}
                    required
                  />
                </div>
              )}
            />
            {parameters.definitions.length > 0 && (
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                sx={{ my: 1 }}
              >
                {parameters.definitions.map((parameter) => (
                  <Chip
                    key={parameter.name}
                    size="small"
                    label={`{{ parameters.${parameter.name} }}`}
                    onClick={() =>
                      setValue(
                        "filename_template",
                        `${watch("filename_template")}{{ parameters.${
                          parameter.name
                        } }}`,
                        { shouldDirty: true }
                      )
                    }
                  />
                ))}
              </Stack>
            )}
            {errors.filename_template && (
              <Typography color="error" variant="caption">
                {errors.filename_template.message}
              </Typography>
            )}
            <div>
              <Controller
                name="save_in_subject_documents"
                control={control}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      label={t("save_in_subject_documents")}
                      control={
                        <Checkbox
                          {...field}
                          value={field.value}
                          checked={field.value}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                        />
                      }
                      className={styles.modalCheckbox}
                    />
                  </>
                )}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Controller
                  name="overwrite_existing_file"
                  control={control}
                  render={({ field }) => (
                    <>
                      <FormControlLabel
                        label={t("overwrite_existing_file")}
                        sx={{ marginRight: "0.5rem" }}
                        control={
                          <Checkbox
                            {...field}
                            value={field.value}
                            checked={field.value}
                            onChange={(event) =>
                              field.onChange(event.target.checked)
                            }
                          />
                        }
                        className={styles.modalCheckbox}
                      />
                    </>
                  )}
                />
                <Tooltip title={t("overwrite_existing_file_info")}>
                  <InfoOutlinedIcon />
                </Tooltip>
              </div>
            </div>
            {shouldSaveinSubjectDocuments && (
              <>
                <Controller
                  name="target_folder"
                  control={control}
                  rules={{ required: t("folder_required_msg") as string }}
                  render={({ field }) => (
                    <div ref={registerValidationTarget("target_folder")}>
                      <DropDown
                        options={folderOptions}
                        useReduxDefaultValue={false}
                        label={t("folder")}
                        mandatory={true}
                        required={true}
                        onChange={(selectedOption: any) => {
                          if (selectedOption && selectedOption.value) {
                            field.onChange(selectedOption.value);
                          }
                        }}
                      />
                    </div>
                  )}
                />
                {errors.target_folder && (
                  <Typography color="error" variant="caption">
                    {errors.target_folder.message}
                  </Typography>
                )}
              </>
            )}
          </Box>
          <Box className={styles.checkboxWrapper}>
            <Controller
              name="is_exclusion_list"
              control={control}
              render={({ field }) => (
                <>
                  <FormControlLabel
                    label={t("is_exclusion_list")}
                    control={
                      <Checkbox
                        {...field}
                        value={field.value}
                        checked={field.value}
                        disabled={selectedTableRows.length === 0}
                        onChange={(event) =>
                          field.onChange(event.target.checked)
                        }
                      />
                    }
                    className={styles.modalCheckbox}
                  />
                </>
              )}
            />
            <Controller
              name="include_children"
              control={control}
              render={({ field }) => (
                <>
                  <FormControlLabel
                    label={t("include_child_companies")}
                    control={
                      <Checkbox
                        {...field}
                        value={field.value}
                        checked={field.value}
                        onChange={(event) =>
                          field.onChange(event.target.checked)
                        }
                        disabled={
                          parentsOnlySelected || selectedTableRows.length === 0
                        }
                      />
                    }
                    className={styles.modalCheckbox}
                  />
                </>
              )}
            />
            <Controller
              name="parents_only"
              control={control}
              render={({ field }) => (
                <>
                  <FormControlLabel
                    label={t("only_parent_companies")}
                    control={
                      <Checkbox
                        {...field}
                        value={field.value}
                        checked={field.value}
                        onChange={(event) =>
                          field.onChange(event.target.checked)
                        }
                        disabled={includeChildrenSelected}
                      />
                    }
                    className={styles.modalCheckbox}
                  />
                </>
              )}
            />
          </Box>
          <div className={styles.infoContainer}>
            <InfoOutlinedIcon />
            <p className={styles.infoText}>
              {t(getTranslationKey(), { count: selectedTableRows.length })}
            </p>
          </div>
          <Box className={styles.buttonWrapper}>
            <GenericButton variant="outlined" onClick={onClose}>
              {t("cancel")}
            </GenericButton>
            <GenericButton
              variant="contained"
              type="submit"
              disabled={!!templateId && !parameters.ready}
            >
              {t("save")}
            </GenericButton>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default CreateDocumentModal;
